import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { MdOutlineAttachFile, MdOutlineRemoveRedEye } from 'react-icons/md';
import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import { Link, Outlet } from 'react-router-dom';
import { MdOutlineDesktopWindows } from 'react-icons/md';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate, getWebChannelSourceLabel } from 'utils/helperFunctions';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import DateRangePicker from 'components/Common/DateRangePicker';
import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';
import { useGetAgencyCallLogs } from '@/apis/agency/callLogs/hook';
import { useGetBusinesses } from '@/apis/agency/hook';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import { BusinessProfileResponse } from '@/apis/business/types';
import { FiMessageSquare } from 'react-icons/fi';
import ReportFlagIcon from '@/assets/icons/ReportFlagIcon';

type ReportStatusFilter = 'reported' | 'all' | 'unreported';

interface FilterFormData {
  call_state: 'active' | 'archived' | 'all';
  report_status: ReportStatusFilter;
  filter: {
    channel_eq: string;
    business_id_eq: string;
    discarded_at_null?: boolean;
    discarded_at_not_null?: boolean;
  };
}

const defaultFormValues: FilterFormData = {
  call_state: 'active',
  report_status: 'all',
  filter: {
    channel_eq: ' ',
    business_id_eq: ' '
  }
};

export default function AgencyChatLogs() {
  const theme = useTheme();
  const { control, handleSubmit, watch, setValue } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [searchText, setSearchText] = useState<null | string>(null);
  const [filterSearchText, setFilterSearchText] = useState<null | string>(null);

  const transformFilters = (formData: FilterFormData) => {
    const filter: Partial<FilterFormData['filter']> = { ...formData.filter };
    Reflect.deleteProperty(filter, 'discarded_at_null');
    Reflect.deleteProperty(filter, 'discarded_at_not_null');
    if (formData.call_state === 'active') {
      filter.discarded_at_null = true;
    } else if (formData.call_state === 'archived') {
      filter.discarded_at_not_null = true;
    }
    const reportStatus = formData.report_status;
    return {
      filter,
      ...(reportStatus && reportStatus !== 'all' ? { 'filter[report_status_eq]': reportStatus } : {})
    };
  };

  const { data: businesses, isLoading: isBusinessesLoading } = useGetBusinesses({
    filter: { account_status_eq: 0 },
    per_page: 9999
  });

  const businessCreatedAtById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of (businesses?.data ?? []) as BusinessProfileResponse[]) {
      if (item.created_at) map[String(item.id)] = item.created_at;
    }
    return map;
  }, [businesses?.data]);

  const businessesOptions = useMemo(
    () =>
      ((businesses?.data ?? []) as BusinessProfileResponse[]).map((business) => ({
        value: business.id,
        label: business.name
      })),
    [businesses?.data]
  );

  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>(transformFilters(defaultFormValues));

  const { data: agencyOrganization } = useGetAgencyOrganizations();

  const watchedValues = watch();
  const tillDateCreatedAt = useMemo(() => {
    const bid = watchedValues?.filter?.business_id_eq;
    if (bid && bid !== ' ' && businessCreatedAtById[String(bid)]) {
      return businessCreatedAtById[String(bid)];
    }
    return agencyOrganization?.created_at;
  }, [watchedValues?.filter?.business_id_eq, businessCreatedAtById, agencyOrganization?.created_at]);

  useEffect(() => {
    const transformedFilters = transformFilters(watchedValues);
    setAppliedFilters((prevFilters) => {
      const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(transformedFilters);
      if (filtersChanged) {
        resetPage();
      }
      return transformedFilters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues), resetPage]);

  useEffect(() => {
    setFilterSearchText(searchText);
    resetPage();
  }, [searchText, resetPage]);

  const onSubmit = (data: FilterFormData) => {
    setAppliedFilters(transformFilters(data));
    resetPage();
  };

  const { data: chatLogs, isLoading: isChatLogsLoading } = useGetAgencyCallLogs(
    {
      include: 'contact,agent,business,intent_signal',
      ...appliedFilters,
      'filter[source_info_or_session_id_cont]': filterSearchText,
      'filter[interaction_type_eq]': 1,
      page: currentPage,
      per_page: perPage
    },
    { refetchInterval: 10000 }
  );

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        marginBottom={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={1}
        sx={{ width: '100%', overflow: 'visible' }}
      >
        <Typography
          variant='h6'
          noWrap
          fontWeight={500}
          sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' }, flexShrink: 0, mt: { lg: 1.5 } }}
        >
          Chat Logs
        </Typography>
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          gap={1}
          flexDirection={{ xs: 'column', lg: 'row' }}
          flexWrap={{ xs: 'wrap', lg: 'nowrap' }}
          alignItems='flex-start'
          sx={{
            width: { xs: '100%', lg: 'auto' },
            minWidth: 0,
            flexShrink: 1,
            overflow: 'visible',
            pt: 1
          }}
        >
          <ToggleButtonGroup
            value={watch('report_status')}
            exclusive
            onChange={(_, value: ReportStatusFilter | null) => {
              if (!value) return;
              setValue('report_status', value, { shouldDirty: true });
            }}
            sx={{
              backgroundColor: '#fff',
              padding: '3px',
              height: 40,
              flexShrink: 0,
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                margin: 0,
                '&:not(:first-of-type)': {
                  borderRadius: '8px',
                  marginLeft: 0
                },
                '&:first-of-type': {
                  borderRadius: '8px'
                }
              },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                color: '#6B7280',
                borderRadius: '8px',
                px: 1.5,
                py: 0.75,
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.primary.light} !important`,
                  color: `${theme.palette.text.primary} !important`
                },
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.03)'
                }
              }
            }}
          >
            <ToggleButton value='reported'>Reported</ToggleButton>
            <ToggleButton value='all'>All</ToggleButton>
            <ToggleButton value='unreported'>Unreported</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ width: { xs: '100%', lg: '280px' }, minWidth: { xs: '100%', lg: '250px' }, flexShrink: 0 }}>
            <DateRangePicker name='filter.calls_received_in' control={control} createdAt={tillDateCreatedAt} />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '250px' }, flexShrink: 0 }}>
            <Search
              size='small'
              sx={{
                minWidth: { xs: '100%', lg: 220 },
                width: { xs: '100%', lg: 'auto' },
                '& .MuiInputBase-root': {
                  backgroundColor: theme.palette.background.paper
                }
              }}
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder='Search by Session ID'
            />
          </Box>
        </Box>
      </Box>
      <Stack direction='row' justifyContent='space-between' spacing={2} mb={2}>
        <Select
          name='filter.business_id_eq'
          label='Business'
          control={control}
          sortOrder='none'
          searchable
          disabled={isBusinessesLoading}
          options={[{ value: ' ', label: 'All' }, ...businessesOptions]}
          sx={{
            height: '40px',
            background: theme.palette.background.paper
          }}
        />
        <Select
          name='filter.channel_eq'
          label='Source'
          sortOrder='none'
          control={control}
          options={[
            { value: ' ', label: 'All' },
            { value: 'web_chat', label: 'Website' },
            { value: 'personalized_link_chat', label: 'Personalized Link' }
          ]}
          sx={{
            height: '40px',
            background: theme.palette.background.paper
          }}
        />
        <Select
          name='call_state'
          label='Chat State'
          sortOrder='none'
          control={control}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' }
          ]}
          sx={{
            height: '40px',
            background: theme.palette.background.paper
          }}
        />
      </Stack>
      <Box sx={{ width: '100%', overflowX: 'auto', mb: { xs: 2, lg: 0 } }}>
        <DataTable data={chatLogs?.data} columns={columns()} isHeaderSticky={true} isLoading={isChatLogsLoading} />
      </Box>
      <Box sx={{ pb: { xs: 2, lg: 0 } }}>
        <Pagination
          totalPages={chatLogs?.meta?.total_pages}
          totalCount={chatLogs?.meta?.total_count ?? 0}
          currentPage={currentPage}
          perPage={perPage}
          onChange={setPage}
          onPerPageChange={setPerPage}
        />
      </Box>
      <Outlet />
    </>
  );
}

const columns = (): ColumnDef<any>[] => [
  {
    header: 'SOURCE',
    size: 220,
    minSize: 180,
    cell: (info) => {
      const { channel, source_info, created_at, report_call: report, test } = info?.row?.original || {};
      const getIconLabel = (sourceType: string) => {
        switch (sourceType) {
          case 'personalized_link_chat':
            return { icon: <MdOutlineAttachFile />, label: 'Personalized Link' };
          case 'phone_chat':
          case 'sms_chat':
            return { icon: <FiMessageSquare />, label: source_info || '-' };
          default:
            return {
              icon: <MdOutlineDesktopWindows />,
              label: getWebChannelSourceLabel(typeof source_info === 'string' ? source_info : undefined, created_at)
            };
        }
      };
      const { icon, label } = getIconLabel(channel);
      return (
        <Box display='flex' gap={1} alignItems='center' fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
          <Box mt='3px' sx={{ flexShrink: 0 }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, whiteSpace: 'nowrap' }}>{label}</Typography>
          {test && (
            <Typography
              component='span'
              sx={{
                fontSize: 11,
                borderRadius: 6,
                px: 1,
                py: 0.25,
                fontWeight: 500,
                color: '#6B7280',
                bgcolor: '#F3F4F6',
                flexShrink: 0
              }}
            >
              Test
            </Typography>
          )}
          {report?.description && (
            <Tooltip title={report.description} arrow placement='top'>
              <Box display='flex' sx={{ flexShrink: 0, lineHeight: 0 }}>
                <ReportFlagIcon />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    }
  },
  {
    header: 'LAST MESSAGE TIME',
    size: 200,
    cell: (info) => (
      <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, whiteSpace: 'nowrap' }}>
        {formatDate(info?.row?.original?.updated_at)}
      </Typography>
    )
  },
  {
    header: 'Business',
    size: 200,
    minSize: 160,
    cell: (info) => {
      const name = info.row.original.business?.name ?? '';
      const words = name.trim().split(/\s+/).filter(Boolean);
      const isTruncated = words.length > 3;
      const displayName = isTruncated ? `${words.slice(0, 3).join(' ')}...` : name;
      return (
        <Tooltip title={isTruncated ? name : ''} placement='top' disableHoverListener={!isTruncated}>
          <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, whiteSpace: 'nowrap' }}>{displayName}</Typography>
        </Tooltip>
      );
    }
  },
  {
    header: 'Agent',
    size: 140,
    cell: (info) => (
      <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, whiteSpace: 'nowrap' }}>
        {info.row.original.agent.name}
      </Typography>
    )
  },
  {
    header: 'NUMBER OF MESSAGES',
    size: 160,
    cell: (info) => (
      <Typography sx={{ fontSize: { xs: '14px', lg: '16px' }, whiteSpace: 'nowrap' }}>
        {info.row.original.interaction_journey_message_count ?? '0'}
      </Typography>
    )
  },
  {
    header: 'ACTION',
    size: 80,
    cell: (info) => (
      <Box display='flex' gap={{ xs: 0.5, lg: 0 }}>
        <IconButton
          component={Link}
          to={`${info.row.original.id}`}
          relative='route'
          sx={{ padding: { xs: '4px', lg: '8px' } }}
        >
          <MdOutlineRemoveRedEye color='#777777' size={24} />
        </IconButton>
      </Box>
    )
  }
];
